import React, { useState, useEffect } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Snackbar,
  CircularProgress,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Card,
  CardContent,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { useNavigate, useLocation } from "react-router-dom";
import { useUserContext } from "./context/UserContext";
import { db } from "../firebase.config";

export default function SelectCreditNotes() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useUserContext();

  const [allCreditNotes, setAllCreditNotes] = useState([]);
  const [selectedCreditNotes, setSelectedCreditNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const [searchText, setSearchText] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterDate, setFilterDate] = useState("all");
  const [filterCreatedDate, setFilterCreatedDate] = useState("all");

  useEffect(() => {
    const fetchCreditNotes = async () => {
      setLoading(true);
      try {
        if (!userId) {
          console.error("User ID is not available");
          setLoading(false);
          return;
        }

        const creditNotesRef = collection(db, "users", userId, "creditNotes");
        const creditNotesSnapshot = await getDocs(creditNotesRef);
        const creditNotesList = creditNotesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setAllCreditNotes(creditNotesList);
      } catch (error) {
        console.error("Error fetching credit notes:", error);
        setSnackbar({
          open: true,
          message: "Error fetching credit notes: " + error.message,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCreditNotes();
  }, [userId]);

  // Filter credit notes based on search and filters
  const filteredCreditNotes = allCreditNotes.filter((cn) => {
    // Search filter - by credit note number or customer name
    const matchesSearch =
      cn.creditNoteNumber?.toLowerCase().includes(searchText.toLowerCase()) ||
      cn.billTo?.name?.toLowerCase().includes(searchText.toLowerCase());

    // Status filter - open or closed credit notes
    const status = cn.status || "open";
    const matchesStatus =
      filterStatus === "all" ||
      (filterStatus === "open" && status === "open") ||
      (filterStatus === "closed" && status === "closed");

    // Date filter - recently created (last 7 days) or by created date
    let matchesCreatedDate = true;
    if (filterCreatedDate !== "all") {
      const createdDate = new Date(cn.createdAt?.toDate?.() || cn.createdAt || cn.creditNoteDate);
      const now = new Date();
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      if (filterCreatedDate === "recently") {
        matchesCreatedDate = createdDate > sevenDaysAgo;
      } else if (filterCreatedDate === "thisMonth") {
        matchesCreatedDate = createdDate > thirtyDaysAgo;
      } else if (filterCreatedDate === "older") {
        matchesCreatedDate = createdDate <= thirtyDaysAgo;
      }
    }

    return matchesSearch && matchesStatus && matchesCreatedDate;
  });

  const handleSelectCreditNote = (creditNote) => {
    const isAlreadySelected = selectedCreditNotes.some((cn) => cn.id === creditNote.id);
    if (isAlreadySelected) {
      setSelectedCreditNotes(
        selectedCreditNotes.filter((cn) => cn.id !== creditNote.id)
      );
    } else {
      setSelectedCreditNotes([...selectedCreditNotes, creditNote]);
    }
  };

  const handleConfirmSelection = () => {
    if (selectedCreditNotes.length === 0) {
      setSnackbar({
        open: true,
        message: "Please select at least one credit note",
      });
      return;
    }

    // You can handle the selected credit notes here
    // For now, just navigate back
    const returnPath = location.state?.from || "/homepage";
    navigate(returnPath, { state: { selectedCreditNotes } });
  };

  const handleCancel = () => {
    const returnPath = location.state?.from || "/homepage";
    navigate(returnPath);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
          Select Credit Notes
        </Typography>

        <Grid container spacing={3}>
          {/* Left Column - Credit Notes Selection */}
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography
                variant="h6"
                sx={{ color: "primary.main", mb: 2, fontWeight: 600 }}
              >
                Select Credit Notes
              </Typography>

              {/* Search and Filter Controls */}
              <Box sx={{ mb: 3 }}>
                {/* Search Field */}
                <TextField
                  fullWidth
                  placeholder="Search and select credit notes..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  size="small"
                  sx={{
                    mb: 2.5,
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#fff",
                      borderRadius: 1,
                      "&:hover fieldset": {
                        borderColor: "#1976d2",
                      },
                    },
                  }}
                />

                {/* Filter Section Header */}
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: "#666",
                    mb: 1.5,
                    textTransform: "uppercase",
                    fontSize: "0.75rem",
                    letterSpacing: 0.5,
                  }}
                >
                  Filters
                </Typography>

                {/* Filters in Horizontal Line */}
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.5 }}>
                  {/* Credit Note Status Filter */}
                  <FormControl
                    fullWidth
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#f8f9fa",
                        borderRadius: 1,
                        transition: "all 0.2s",
                        height: "36px",
                        "&:hover": {
                          backgroundColor: "#f0f0f0",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "#fff",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "0.85rem",
                      },
                    }}
                  >
                    <InputLabel sx={{ fontSize: "0.85rem", fontWeight: 500 }}>
                      Credit Note Status
                    </InputLabel>
                    <Select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      label="Credit Note Status"
                    >
                      <MenuItem value="all">All Credit Notes</MenuItem>
                      <MenuItem value="open">Open Credit Notes</MenuItem>
                      <MenuItem value="closed">Closed Credit Notes</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Date Created Filter */}
                  <FormControl
                    fullWidth
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#f8f9fa",
                        borderRadius: 1,
                        transition: "all 0.2s",
                        height: "36px",
                        "&:hover": {
                          backgroundColor: "#f0f0f0",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "#fff",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "0.85rem",
                      },
                    }}
                  >
                    <InputLabel sx={{ fontSize: "0.85rem", fontWeight: 500 }}>
                      Date Created
                    </InputLabel>
                    <Select
                      value={filterCreatedDate}
                      onChange={(e) => setFilterCreatedDate(e.target.value)}
                      label="Date Created"
                    >
                      <MenuItem value="all">All Dates</MenuItem>
                      <MenuItem value="recently">Recently Created (Last 7 days)</MenuItem>
                      <MenuItem value="thisMonth">This Month (Last 30 days)</MenuItem>
                      <MenuItem value="older">Older than 30 days</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Additional Date Filter */}
                  <FormControl
                    fullWidth
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#f8f9fa",
                        borderRadius: 1,
                        transition: "all 0.2s",
                        height: "36px",
                        "&:hover": {
                          backgroundColor: "#f0f0f0",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "#fff",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "0.85rem",
                      },
                    }}
                  >
                    <InputLabel sx={{ fontSize: "0.85rem", fontWeight: 500 }}>
                      Filter by Date
                    </InputLabel>
                    <Select
                      value={filterDate}
                      onChange={(e) => setFilterDate(e.target.value)}
                      label="Filter by Date"
                    >
                      <MenuItem value="all">All Credit Notes</MenuItem>
                      <MenuItem value="recent">Recent (Last 30 days)</MenuItem>
                      <MenuItem value="old">Older (More than 30 days)</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* Credit Notes List */}
              <Box sx={{ maxHeight: "500px", overflowY: "auto", border: "1px solid #e0e0e0", borderRadius: 1 }}>
                {filteredCreditNotes.length === 0 ? (
                  <Box sx={{ p: 2, textAlign: "center", color: "#999" }}>
                    <Typography variant="body2">
                      No credit notes found. Create one first.
                    </Typography>
                  </Box>
                ) : (
                  filteredCreditNotes.map((creditNote) => (
                    <Card
                      key={creditNote.id}
                      sx={{
                        mb: 1,
                        cursor: "pointer",
                        backgroundColor: selectedCreditNotes.some(
                          (cn) => cn.id === creditNote.id
                        )
                          ? "#e3f2fd"
                          : "#fff",
                        border: selectedCreditNotes.some(
                          (cn) => cn.id === creditNote.id
                        )
                          ? "2px solid #1976d2"
                          : "1px solid #e0e0e0",
                        transition: "all 0.2s",
                        "&:hover": {
                          boxShadow: 2,
                          backgroundColor: selectedCreditNotes.some(
                            (cn) => cn.id === creditNote.id
                          )
                            ? "#e3f2fd"
                            : "#f5f5f5",
                        },
                      }}
                      onClick={() => handleSelectCreditNote(creditNote)}
                    >
                      <CardContent sx={{ p: 1.5 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {creditNote.creditNoteNumber}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#666", fontSize: "0.85rem" }}>
                              {creditNote.billTo?.name}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            ₹{creditNote.totals?.grandTotal?.toFixed(2) || "0.00"}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  ))
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Right Column - Selected Details */}
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography
                variant="h6"
                sx={{ color: "#9c27b0", mb: 2, fontWeight: 600 }}
              >
                Selected Details ({selectedCreditNotes.length})
              </Typography>

              <Box
                sx={{
                  maxHeight: "500px",
                  overflowY: "auto",
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  p: 2,
                  backgroundColor: "#fafafa",
                  minHeight: "500px",
                }}
              >
                {selectedCreditNotes.length === 0 ? (
                  <Typography
                    variant="body2"
                    sx={{ textAlign: "center", color: "#999", mt: 10 }}
                  >
                    Details of selected credit notes will appear here.
                  </Typography>
                ) : (
                  <Box>
                    {selectedCreditNotes.map((creditNote) => (
                      <Card key={creditNote.id} sx={{ mb: 2, backgroundColor: "#fff" }}>
                        <CardContent sx={{ p: 1.5 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                            {creditNote.creditNoteNumber}
                          </Typography>
                          <Box sx={{ fontSize: "0.85rem", color: "#666" }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                              <span>Date:</span>
                              <span sx={{ fontWeight: 600 }}>{creditNote.creditNoteDate}</span>
                            </Box>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                              <span>Bill To:</span>
                              <span>{creditNote.billTo?.name}</span>
                            </Box>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                              <span>Items:</span>
                              <span>{creditNote.items?.length || 0}</span>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                fontWeight: 600,
                                pt: 1,
                                borderTop: "1px solid #e0e0e0",
                              }}
                            >
                              <span>Grand Total:</span>
                              <span>₹{creditNote.totals?.grandTotal?.toFixed(2) || "0.00"}</span>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 3 }}>
          <Button
            variant="outlined"
            color="error"
            onClick={handleCancel}
            sx={{ px: 3 }}
          >
            CANCEL
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmSelection}
            disabled={selectedCreditNotes.length === 0}
            sx={{ px: 3 }}
          >
            CONFIRM SELECTION
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Container>
  );
}

